/**
 * ============================================================================
 * AGE GATE MANAGER
 * ============================================================================
 *
 * Manages age verification for tobacco/alcohol products.
 * Integrates with the existing modal system and localStorage.
 *
 * Features:
 * - Button-based age verification
 * - localStorage persistence with expiration
 * - Session fallback
 * - Customizable redirect on decline
 * - Event-based architecture
 * - Accessibility compliant
 *
 * ============================================================================
 */

class AgeGateManager {
  constructor(options = {}) {
    this.config = {
      storageKey: 'theme:age-verified',
      sessionKey: 'session:age-verified',
      modalId: 'age-gate-modal',
      minAge: 18,
      cookieDays: 30,
      declineUrl: 'https://www.google.com',
      enabled: true,
      ...options
    };

    this.modal = null;
    this.isVerified = false;

    this.init();
  }

  /**
   * Initialize age gate manager
   */
  init() {
    console.log('🔞 Age Gate Manager initializing...');

    // Find modal element
    this.modal = document.getElementById(this.config.modalId);

    if (!this.modal) {
      console.warn('Age Gate: Modal element not found');
      return;
    }

    // Read config from modal data attributes
    this.readConfigFromModal();

    // Check if enabled
    if (!this.config.enabled) {
      console.log('Age Gate: Disabled via settings');
      return;
    }

    // Check verification status
    if (this.checkVerification()) {
      console.log('✅ Age Gate: User already verified');
      this.isVerified = true;
      return;
    }

    // Show modal and bind events
    this.showModal();
    this.bindEvents();

    console.log('✅ Age Gate Manager initialized');
  }

  /**
   * Read configuration from modal data attributes
   */
  readConfigFromModal() {
    if (!this.modal) return;

    const minAge = this.modal.getAttribute('data-min-age');
    const cookieDays = this.modal.getAttribute('data-cookie-days');
    const declineUrl = this.modal.getAttribute('data-decline-url');

    if (minAge) this.config.minAge = parseInt(minAge);
    if (cookieDays) this.config.cookieDays = parseInt(cookieDays);
    if (declineUrl) this.config.declineUrl = declineUrl;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (!this.modal) return;

    // Confirm button
    const confirmBtn = this.modal.querySelector('[data-age-gate-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.handleConfirm());
    }

    // Decline button
    const declineBtn = this.modal.querySelector('[data-age-gate-decline]');
    if (declineBtn) {
      declineBtn.addEventListener('click', () => this.handleDecline());
    }

    // Prevent closing modal by clicking overlay
    const overlay = this.modal.querySelector('.modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
      });
    }

    // Prevent ESC key from closing
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.modal.hasAttribute('open')) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', this.escapeHandler, true);
  }

  /**
   * Show age gate modal
   */
  showModal() {
    if (!this.modal) return;

    // Lock body scroll
    document.documentElement.classList.add('lock-all');

    // Show modal
    this.modal.setAttribute('open', '');
    if (this.modal.open !== undefined) {
      this.modal.open = true;
    }

    // Emit event
    this.emitEvent('show');
  }

  /**
   * Hide age gate modal
   */
  hideModal() {
    if (!this.modal) return;

    // Unlock body scroll
    document.documentElement.classList.remove('lock-all');

    // Hide modal
    this.modal.removeAttribute('open');
    if (this.modal.open !== undefined) {
      this.modal.open = false;
    }

    // Remove escape handler
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler, true);
    }

    // Emit event
    this.emitEvent('hide');
  }

  /**
   * Handle confirm button click
   */
  handleConfirm() {
    console.log('✅ Age Gate: User confirmed age');

    this.setVerified();
    this.isVerified = true;
    this.hideModal();

    // Emit event
    this.emitEvent('verified', {
      age: this.config.minAge,
      timestamp: new Date().getTime()
    });
  }

  /**
   * Handle decline button click
   */
  handleDecline() {
    console.log('❌ Age Gate: User declined age verification');

    // Emit event before redirect
    this.emitEvent('declined', {
      age: this.config.minAge,
      redirectUrl: this.config.declineUrl
    });

    // Redirect after short delay
    setTimeout(() => {
      window.location.href = this.config.declineUrl;
    }, 300);
  }

  /**
   * Check if user is verified
   * @returns {boolean} True if verified
   */
  checkVerification() {
    // Check localStorage first
    if (this.checkLocalStorage()) {
      return true;
    }

    // Fallback to sessionStorage
    if (this.checkSessionStorage()) {
      return true;
    }

    return false;
  }

  /**
   * Check localStorage for verification
   * @returns {boolean} True if verified
   */
  checkLocalStorage() {
    try {
      const stored = localStorage.getItem(this.config.storageKey);
      if (!stored) return false;

      const data = JSON.parse(stored);
      const now = new Date().getTime();

      // Check if verification is still valid
      if (data.verified && data.expires && data.expires > now) {
        return true;
      }

      // Expired, remove it
      localStorage.removeItem(this.config.storageKey);
      return false;
    } catch (e) {
      console.warn('Age Gate: localStorage not available', e);
      return false;
    }
  }

  /**
   * Check sessionStorage for verification
   * @returns {boolean} True if verified
   */
  checkSessionStorage() {
    try {
      const stored = sessionStorage.getItem(this.config.sessionKey);
      return stored === 'true';
    } catch (e) {
      console.warn('Age Gate: sessionStorage not available', e);
      return false;
    }
  }

  /**
   * Set user as verified
   */
  setVerified() {
    const now = new Date().getTime();
    const expires = now + (this.config.cookieDays * 24 * 60 * 60 * 1000);

    const data = {
      verified: true,
      timestamp: now,
      expires: expires,
      minAge: this.config.minAge
    };

    // Try localStorage first
    try {
      localStorage.setItem(this.config.storageKey, JSON.stringify(data));
    } catch (e) {
      console.warn('Age Gate: Could not save to localStorage, using sessionStorage', e);

      // Fallback to sessionStorage
      try {
        sessionStorage.setItem(this.config.sessionKey, 'true');
      } catch (e2) {
        console.error('Age Gate: Could not save verification', e2);
      }
    }
  }

  /**
   * Clear verification (for testing)
   */
  clearVerification() {
    try {
      localStorage.removeItem(this.config.storageKey);
      sessionStorage.removeItem(this.config.sessionKey);
      console.log('🧹 Age Gate: Verification cleared');
    } catch (e) {
      console.warn('Age Gate: Could not clear verification', e);
    }
  }

  /**
   * Emit custom event
   * @param {string} eventName - Event name
   * @param {Object} detail - Event detail
   */
  emitEvent(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(`age-gate:${eventName}`, {
      detail: {
        ...detail,
        manager: this
      }
    }));
  }

  /**
   * Get verification status
   * @returns {Object} Verification status
   */
  getStatus() {
    return {
      isVerified: this.isVerified,
      minAge: this.config.minAge,
      cookieDays: this.config.cookieDays,
      enabled: this.config.enabled
    };
  }
}

// Make AgeGateManager available globally
window.AgeGateManager = AgeGateManager;

